"use client";

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Stethoscope } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const teamMembers = [{
  name: "Marcus Thorne",
  position: "Lead Operations Manager",
  bio: "With over 8 years in Ottawa logistics, Marcus ensures every residential pickup is handled with precision. He specializes in heavy appliance removal and eco-friendly sorting, making sure your junk finds the right second home.",
  credential: "Fully Insured",
  credential2: "Removal Certification",
  credential3: "8+ Years Experience",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/crew_marcus_thorne.png"
}, {
  name: "Liam O'Malley",
  position: "Senior Removal Specialist",
  bio: "Liam is our go-to expert for complex commercial clear-outs. Known for his friendly attitude and efficiency, he leads our Kanata and Stittsville teams with a focus on safety and rapid same-day service.",
  credential: "Fully Insured",
  credential2: "Safety Certified",
  credential3: "Local Expert",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/crew_liam_omalley.png"
}];
export default function OurCompassionateExperts() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="227299" id="meet-the-crew" className="py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div ref={headerReveal.ref} className={cn("max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ease-out", headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <Stethoscope className="w-5 h-5 text-primary" />
            <span className="text-sm font-semibold tracking-widest uppercase text-primary font-mono">
              The JUNKR Standard
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-default font-bold text-foreground mb-6 tracking-tighter">
            Meet Your Friendly Local Team
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            Our Ottawa-based team is professional, friendly, and ready to do the heavy lifting. 
            We treat your home with respect and your junk with environmental responsibility.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {teamMembers.map((member, i) => <TeamMemberCard key={member.name} member={member} index={i} />)}
        </div>
      </div>
    </section>;
}
function TeamMemberCard({
  member,
  index
}: {
  member: (typeof teamMembers)[0];
  index: number;
}) {
  const cardReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const [isOpen, setIsOpen] = useState(false);
  return <div data-index={index} ref={cardReveal.ref} className={cn("transition-all duration-1000 ease-out delay-[calc(100ms*var(--index))]", cardReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
    "--index": index
  } as React.CSSProperties}>
      <Card className="group h-full overflow-hidden border border-border bg-card shadow-lg hover:shadow-4xl transition-all duration-500 rounded-3xl flex flex-col">
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <img src={member.image} data-wvc-srcset={`${member.image}-wvc-srcset`} data-wvc-sizes={`${member.image}-wvc-sizes`} alt={`Portrait of ${member.name}`} loading="lazy" className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out scale-105 group-hover:scale-100" />
          <div className="absolute inset-0 bg-gradient-to-t from-card/95 via-card/10 to-transparent opacity-90" />
        </div>

        <CardContent className="p-8 flex-grow flex flex-col relative z-10 -mt-12 bg-card/95 backdrop-blur-md rounded-t-3xl mx-2 border border-border shadow-md">
          <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-4">
            <div>
              <h3 className="text-2xl font-default font-bold text-foreground mb-1 tracking-tight">
                {member.name}
              </h3>
              <p className="text-primary font-semibold text-sm font-default">
                {member.position}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary/90 border-transparent px-3 py-1 text-[10px] font-bold shadow-md rounded-lg uppercase tracking-wider">
                {member.credential}
              </Badge>
              <Badge className="bg-primary text-primary-foreground hover:bg-primary/90 border-transparent px-3 py-1 text-[10px] font-bold shadow-md rounded-lg uppercase tracking-wider">
                {member.credential2}
              </Badge>
              <Badge className="bg-accent text-accent-foreground hover:bg-accent/90 border-transparent px-3 py-1 text-[10px] font-bold shadow-md rounded-lg uppercase tracking-wider">
                {member.credential3}
              </Badge>
            </div>
          </div>

          <Collapsible open={isOpen} onOpenChange={setIsOpen} className="flex flex-col flex-grow justify-end mt-auto">
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapse data-[state=open]:animate-expand">
              <p className="text-muted-foreground text-sm leading-relaxed pb-6 border-b border-border mb-4 font-default">
                {member.bio}
              </p>
            </CollapsibleContent>
            
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between items-center text-muted-foreground hover:text-foreground hover:bg-accent rounded-xl group/btn h-12 transition-colors">
                <span className="font-bold font-default">
                  {isOpen ? "Less Info" : "View Profile"}
                </span>
                <ChevronDown className={cn("h-5 w-5 transition-transform duration-300", isOpen ? "rotate-180" : "")} />
              </Button>
            </CollapsibleTrigger>
          </Collapsible>
        </CardContent>
      </Card>
    </div>;
}